import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  FormControlLabel,
  Alert,
  Box,
  Button,
  Switch,
  Stepper,
  Step,
  StepButton,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import HeaderTypePicker, { type HeaderTypeId } from './HeaderTypePicker';
import HeaderCommonSettings from './HeaderCommonSettings';
import HeaderExclusiveSettings from './HeaderExclusiveSettings';
import type { EcommerceTabId } from '../navigationConfig';
import { RelatedSettingsLinks, SettingsGuideBanner } from './SettingsPanelChrome';
import {
  getHeaderTemplate,
  getHeaderTemplatesByGroup,
  HEADER_GROUP_ORDER,
} from '../utils/headerTemplates';

interface HeaderSettingsProps {
  config: any;
  defaultConfig: any;
  currentLogoUrl: string | null;
  onHeaderSettingChange: (setting: any, value: any) => void;
  onOpenLogoLibrary: (type: 'desktop' | 'mobile') => void;
  onOpenIconModal: (cardType: 'card1' | 'card2', category: 'phone' | 'cart') => void;
  onRemoveLogo?: () => void; // Logo kaldırma fonksiyonu
  configId?: string; // ✅ Sayfa seçici için e-ticaret config id'si
  onNavigate?: (tab: EcommerceTabId) => void;
}

// Sihirbaz adımları
const STEP_TEMPLATE = 0;
const STEP_APPEARANCE = 1;
const STEP_CONTENT = 2;
const STEP_ICONS = 3;

/**
 * Header ayar paneli — adım adım sihirbaz.
 * Kullanıcı önce şablonu seçer, sonra Görünüm → İçerik → İkonlar adımlarında
 * yalnızca ilgili ayarları görür. Şablon yetenekleri kayıttan (headerTemplates)
 * gelir; API ve otomatik kayıt (ECommercePage) değişmez.
 */
const HeaderSettings: React.FC<HeaderSettingsProps> = ({
  config,
  defaultConfig,
  currentLogoUrl,
  onHeaderSettingChange,
  onOpenLogoLibrary,
  onOpenIconModal,
  onRemoveLogo,
  configId,
  onNavigate,
}) => {
  const { t } = useTranslation('salesSystem');
  const [activeStep, setActiveStep] = React.useState<number>(STEP_TEMPLATE);

  const template = getHeaderTemplate(config.headerSettings?.headerType);
  const caps = template.capabilities;
  const guideKey = caps.builtInMegaMenu
    ? 'header5'
    : caps.overlay
      ? 'header7'
      : caps.inlineNav
        ? 'header6'
        : 'classic';

  const headerEnabled = config.headerSettings.enabled !== false;

  const steps = [
    { key: 'template', label: t('ecommerceHeaderSettings.wizard.template', { defaultValue: 'Şablon' }) },
    { key: 'appearance', label: t('ecommerceHeaderSettings.wizard.appearance', { defaultValue: 'Görünüm' }) },
    { key: 'content', label: t('ecommerceHeaderSettings.wizard.content', { defaultValue: 'İçerik' }) },
    { key: 'icons', label: t('ecommerceHeaderSettings.wizard.icons', { defaultValue: 'İkonlar' }) },
  ];

  // Şablon picker gruplarını kayıttan türet (başlık/ipuçları i18n'den).
  const pickerGroups = HEADER_GROUP_ORDER.map((groupId) => ({
    id: groupId,
    title: t(`ecommerceHeaderSettings.typePicker.${groupId}`),
    hint: t(`ecommerceHeaderSettings.typePicker.${groupId}Hint`),
    options: getHeaderTemplatesByGroup(groupId).map((tpl) => ({
      id: tpl.id,
      title: t(`ecommerceHeaderSettings.headerTypes.${tpl.id}`),
      hint: t(`ecommerceHeaderSettings.typePicker.hints.${tpl.id}`),
      bestFor: t(`ecommerceHeaderSettings.typePicker.bestFor.${tpl.id}`),
    })),
  }));

  const commonProps = {
    config,
    defaultConfig,
    currentLogoUrl,
    onHeaderSettingChange,
    onOpenLogoLibrary,
    onRemoveLogo,
  };

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {t('ecommerceHeaderSettings.title')}
        </Typography>
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Switch
              checked={config.headerSettings.enabled !== undefined ? config.headerSettings.enabled : true}
              onChange={(e) => onHeaderSettingChange('enabled', e.target.checked)}
            />
          }
          label={t('ecommerceHeaderSettings.headerActive')}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceHeaderSettings.panelIntro')}
      </Typography>

      {headerEnabled ? (
        <>
          <Stepper nonLinear activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((step, index) => (
              <Step key={step.key} completed={false}>
                <StepButton color="inherit" onClick={() => setActiveStep(index)}>
                  {step.label}
                </StepButton>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ minHeight: 240 }}>
            {activeStep === STEP_TEMPLATE && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {t('ecommerceHeaderSettings.headerType')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('ecommerceHeaderSettings.typePicker.help')}
                </Typography>
                <HeaderTypePicker
                  value={config.headerSettings.headerType}
                  onChange={(next: HeaderTypeId) => onHeaderSettingChange('headerType', next)}
                  groups={pickerGroups}
                />

                {caps.builtInMegaMenu && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('ecommerceHeaderSettings.header5GridWarning')}
                  </Alert>
                )}

                {guideKey === 'header5' && (
                  <SettingsGuideBanner
                    title={t('ecommerceHeaderSettings.guides.header5.title')}
                    body={t('ecommerceHeaderSettings.guides.header5.body')}
                    actionLabel={t('ecommerceHeaderSettings.guides.header5.action')}
                    onAction={() => onNavigate?.('categories')}
                  />
                )}
                {guideKey === 'header6' && (
                  <SettingsGuideBanner
                    title={t('ecommerceHeaderSettings.guides.header6.title')}
                    body={t('ecommerceHeaderSettings.guides.header6.body')}
                    actionLabel={t('ecommerceHeaderSettings.guides.header6.action')}
                    onAction={() => setActiveStep(STEP_CONTENT)}
                  />
                )}
                {guideKey === 'header7' && (
                  <SettingsGuideBanner
                    title={t('ecommerceHeaderSettings.guides.header7.title')}
                    body={t('ecommerceHeaderSettings.guides.header7.body')}
                    actionLabel={t('ecommerceHeaderSettings.guides.header7.action')}
                    onAction={() => setActiveStep(STEP_CONTENT)}
                  />
                )}
                {guideKey === 'classic' && (
                  <SettingsGuideBanner
                    title={t('ecommerceHeaderSettings.guides.classic.title')}
                    body={t('ecommerceHeaderSettings.guides.classic.body')}
                    actionLabel={t('ecommerceHeaderSettings.guides.classic.action')}
                    onAction={() => onNavigate?.('menus')}
                  />
                )}
              </Box>
            )}

            {activeStep === STEP_APPEARANCE && (
              <Box>
                <HeaderCommonSettings section="appearance" {...commonProps} />
                <Divider sx={{ my: 3 }} />
                <Typography id="header-section-logo" variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {t('ecommerceHeaderSettings.logoSettings')}
                </Typography>
                <HeaderCommonSettings section="logo" {...commonProps} />
              </Box>
            )}

            {activeStep === STEP_CONTENT && (
              <Box>
                <HeaderExclusiveSettings
                  config={config}
                  onHeaderSettingChange={onHeaderSettingChange}
                  onOpenIconModal={onOpenIconModal}
                  configId={configId}
                />
                <HeaderCommonSettings section="visibility" {...commonProps} />
                <Box sx={{ mt: 2 }}>
                  <RelatedSettingsLinks
                    title={t('ecommerceHeaderSettings.relatedTitle')}
                    onNavigate={onNavigate}
                    links={[
                      { tab: 'menus', label: t('ecommerceNav.items.menus') },
                      { tab: 'corporate-nav', label: t('ecommerceNav.items.corporateNav') },
                      { tab: 'categories', label: t('ecommerceNav.items.categories') },
                    ]}
                  />
                </Box>
              </Box>
            )}

            {activeStep === STEP_ICONS && (
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {t('ecommerceHeaderSettings.iconSettings')}
                </Typography>
                <HeaderCommonSettings section="icons" {...commonProps} />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              disabled={activeStep === STEP_TEMPLATE}
              onClick={() => setActiveStep((prev) => Math.max(STEP_TEMPLATE, prev - 1))}
            >
              {t('ecommerceHeaderSettings.wizard.back', { defaultValue: 'Geri' })}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {t('ecommerceHeaderSettings.wizard.stepIndicator', {
                defaultValue: '{{current}} / {{total}}',
                current: activeStep + 1,
                total: steps.length,
              })}
            </Typography>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              disabled={activeStep === STEP_ICONS}
              onClick={() => setActiveStep((prev) => Math.min(STEP_ICONS, prev + 1))}
            >
              {t('ecommerceHeaderSettings.wizard.next', { defaultValue: 'İleri' })}
            </Button>
          </Box>
        </>
      ) : (
        <Alert severity="info">
          {t('ecommerceHeaderSettings.headerDisabledMessage')} <strong>{t('ecommerceHeaderSettings.headerActive')}</strong> {t('ecommerceHeaderSettings.headerDisabledMessageSuffix')}
        </Alert>
      )}
    </Paper>
  );
};

export default HeaderSettings;
